import React from 'react';
import { Clock, TrendingUp, Users, Calendar, AlertTriangle, CheckCircle } from 'lucide-react';

const StatistiquesRetards = ({ retardsEtudiants, retardsEnseignants, activeTab }) => {
  // Calculer les statistiques
  const totalRetards = activeTab === 'etudiants' ? retardsEtudiants.length : retardsEnseignants.length;
  const retardsAujourdhui = activeTab === 'etudiants' 
    ? retardsEtudiants.filter(r => {
        const today = new Date().toISOString().split('T')[0];
        return r.date === today;
      }).length
    : retardsEnseignants.filter(r => {
        const today = new Date().toISOString().split('T')[0];
        return r.date === today;
      }).length;

  const retardsCetteSemaine = activeTab === 'etudiants'
    ? retardsEtudiants.filter(r => {
        const retardDate = new Date(r.date);
        const today = new Date();
        const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
        return retardDate >= weekStart;
      }).length
    : retardsEnseignants.filter(r => {
        const retardDate = new Date(r.date);
        const today = new Date();
        const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
        return retardDate >= weekStart;
      }).length;

  const retardsCeMois = activeTab === 'etudiants'
    ? retardsEtudiants.filter(r => {
        const retardDate = new Date(r.date);
        const today = new Date();
        return retardDate.getMonth() === today.getMonth() && retardDate.getFullYear() === today.getFullYear();
      }).length
    : retardsEnseignants.filter(r => {
        const retardDate = new Date(r.date);
        const today = new Date();
        return retardDate.getMonth() === today.getMonth() && retardDate.getFullYear() === today.getFullYear();
      }).length;

  // Calculer les top retardataires
  const retardsParPersonne = {};
  const currentData = activeTab === 'etudiants' ? retardsEtudiants : retardsEnseignants;
  
  currentData.forEach(retard => {
    const nom = activeTab === 'etudiants' 
      ? `${retard.etudiant?.prenom} ${retard.etudiant?.nom}`
      : `${retard.enseignant?.prenom} ${retard.enseignant?.nom}`;
    retardsParPersonne[nom] = (retardsParPersonne[nom] || 0) + 1;
  });

  const topRetardataires = Object.entries(retardsParPersonne)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3);

  const cardsData = [
    {
      title: "Total Retards",
      value: totalRetards,
      icon: Clock,
      color: "bg-gradient-to-br from-blue-500 to-blue-600",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "Aujourd'hui",
      value: retardsAujourdhui,
      icon: Calendar,
      color: "bg-gradient-to-br from-green-500 to-green-600",
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Cette Semaine",
      value: retardsCetteSemaine,
      icon: TrendingUp,
      color: "bg-gradient-to-br from-orange-500 to-orange-600",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    {
      title: "Ce Mois",
      value: retardsCeMois,
      icon: AlertTriangle,
      color: "bg-gradient-to-br from-red-500 to-red-600",
      iconBg: "bg-red-100",
      iconColor: "text-red-600"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cardsData.map((card, index) => (
          <div key={index} className="relative group">
            <div className={`${card.color} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium mb-1">{card.title}</p>
                  <p className="text-3xl font-bold">{card.value}</p>
                </div>
                <div className={`${card.iconBg} rounded-full p-3`}>
                  <card.icon className={`h-6 w-6 ${card.iconColor}`} />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <div className="flex-1 bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white/80 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((card.value / Math.max(totalRetards, 1)) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section des top retardataires */}
      {topRetardataires.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#202654]" />
              Top Retardataires
            </h3>
            <div className="bg-[#202654] text-white px-3 py-1 rounded-full text-sm font-medium">
              {activeTab === 'etudiants' ? 'Étudiants' : 'Enseignants'}
            </div>
          </div>
          
          <div className="space-y-4">
            {topRetardataires.map(([nom, count], index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      'bg-orange-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{nom}</p>
                      <p className="text-sm text-gray-600">{count} retard{count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-sm font-medium">
                      {count}
                    </div>
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(count / Math.max(...Object.values(retardsParPersonne))) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message d'encouragement */}
      {totalRetards === 0 && (
        <div className="text-center py-12">
          <div className="bg-green-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Excellent ! Aucun retard enregistré
          </h3>
          <p className="text-gray-600">
            Continuez sur cette belle lancée ! 🎉
          </p>
        </div>
      )}
    </div>
  );
};

export default StatistiquesRetards; 